import { ToolHandler, ToolApplyContext } from './tool-handler.interface'
import { ToolDisplayType, ToolConfig, SetupGuideAiMessage } from '../types/ai-chat.types'
import { BrandingColorsHandler } from './handlers/branding-colors.handler'
import { CustomCssHandler } from './handlers/custom-css.handler'
import { SearchArticlesHandler } from './handlers/search-articles.handler'
import { ArticleContentHandler } from './handlers/article-content.handler'
import { GenerateImageHandler } from './handlers/generate-image.handler'
import { AiImageCache } from '../utils/ai-image-cache.util'

const DEFAULT_GENERATING_KEY = 'SETUP_GUIDE.AI_CHAT.TOOL.DEFAULT.GENERATING'
const DEFAULT_RESULT_KEY = 'SETUP_GUIDE.AI_CHAT.TOOL.DEFAULT.RESULT'

export class ToolRegistry {
  private handlers = new Map<string, ToolHandler>()
  readonly imageCache = new AiImageCache()
  private imageHandler = new GenerateImageHandler(this.imageCache)

  register(handler: ToolHandler): void {
    this.handlers.set(handler.name, handler)
  }

  private getHandler(toolName: string): ToolHandler | undefined {
    const handler = this.handlers.get(toolName)
    if (handler) {
      return handler
    }
    if (this.imageHandler.matches(toolName)) {
      return this.imageHandler
    }
    return undefined
  }

  has(toolName: string): boolean {
    return this.getHandler(toolName) !== undefined
  }

  getDisplayType(toolName: string): ToolDisplayType {
    return this.getHandler(toolName)?.config.displayType ?? 'key-value'
  }

  getConfig(toolName: string): ToolConfig | undefined {
    return this.getHandler(toolName)?.config
  }

  getGeneratingMessage(toolName: string): string {
    const handler = this.getHandler(toolName)
    if (!handler) return DEFAULT_GENERATING_KEY
    if (handler instanceof GenerateImageHandler) {
      return handler.getConfigForTool(toolName).generatingKey
    }
    return handler.config.generatingKey
  }

  getResultMessage(toolName: string): string {
    const handler = this.getHandler(toolName)
    if (!handler) return DEFAULT_RESULT_KEY
    if (handler instanceof GenerateImageHandler) {
      return handler.getConfigForTool(toolName).resultKey
    }
    return handler.config.resultKey
  }

  getPreviewData(toolName: string, message: SetupGuideAiMessage): unknown {
    const handler = this.getHandler(toolName)
    if (!handler) {
      return this.getDefaultPreviewLines(message)
    }
    return handler.getPreviewData(message)
  }

  canApply(toolName: string, message: SetupGuideAiMessage): boolean {
    const handler = this.getHandler(toolName)
    if (!handler) {
      return false
    }
    return handler.canApply(message)
  }

  apply(toolName: string, message: SetupGuideAiMessage, context: ToolApplyContext): void {
    const handler = this.getHandler(toolName)
    if (!handler) {
      return
    }
    handler.apply(message, context)
  }

  private getDefaultPreviewLines(message: SetupGuideAiMessage): string[] {
    if (!message.toolContent || typeof message.toolContent !== 'object') {
      return []
    }
    return Object.entries(message.toolContent).map(([key, value]) => `${key}: ${String(value)}`)
  }

  getAllToolConfigs(dashboardPage?: string): ToolConfig[] {
    const configs: ToolConfig[] = []
    this.handlers.forEach((handler) => configs.push(handler.config))
    configs.push(...this.imageHandler.configs)
    if (dashboardPage) {
      return configs.filter((config) => config.dashboardPage === dashboardPage)
    }
    return configs
  }

  getAvailableToolNames(dashboardPage?: string): string[] {
    const names: string[] = []
    this.handlers.forEach((handler) => {
      if (!dashboardPage || handler.config.dashboardPage === dashboardPage) {
        names.push(handler.name)
      }
    })
    if (!dashboardPage || this.imageHandler.configs.some((c) => c.dashboardPage === dashboardPage)) {
      names.push(...this.imageHandler.toolNames)
    }
    return names
  }

  hasGeneratedImage(messages: SetupGuideAiMessage[]): boolean {
    return messages.some(
      (message) =>
        message.type === 'tool_result' &&
        (message.toolName === 'generate_logo' || message.toolName === 'generate_banner'),
    )
  }
}

export function createToolRegistry(isStaffSuperUserDistributor: boolean): ToolRegistry {
  const registry = new ToolRegistry()
  registry.register(new BrandingColorsHandler())
  registry.register(new SearchArticlesHandler())
  registry.register(new ArticleContentHandler())

  if (isStaffSuperUserDistributor) {
    registry.register(new CustomCssHandler())
  }
  return registry
}

export { AiImageCache } from '../utils/ai-image-cache.util'
